import {useState} from 'react';

/**
 * A custom hook that manages the open/close state of an accordion component.
 * This hook provides a boolean state `isOpen` to track whether the accordion body
 * is currently expanded or collapsed. It also provides a `handleToggle` function
 * that inverts the state, enabling accordion toggle functionality when bound to
 * a button or clickable element.
 *
 * @returns An object containing:
 * - `isOpen`: A boolean indicating whether the accordion is currently open.
 * - `handleToggle`: A function that toggles the accordion's open/close state.
 *
 * @example
 * ```tsx
 * const { isOpen, handleToggle } = useAccordion();
 *
 * return (
 *   <button onClick={handleToggle}>
 *     {isOpen ? 'Collapse' : 'Expand'}
 *   </button>
 * );
 * ```
 */
export const useAccordion = () => {
    const [isOpen, setIsOpen] = useState(false);
    /**
     * Toggles the accordion's open/close state.
     * This function inverts the current `isOpen` state. If the accordion is closed,
     * it will be opened, and if it is open, it will be closed. Typically used as the
     * `onClick` handler for the accordion header button.
     *
     * @example
     * ```tsx
     * <button onClick={handleToggle}>
     *   {isOpen ? 'Collapse' : 'Expand'}
     * </button>
     * ```
     */
    const handleToggle = () => {
        setIsOpen(prev => !prev);
    };

    return {
        handleToggle,
        isOpen
    };
};